import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const MAX_JARS_PER_ORDER = 20;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const totalNow = prev.reduce((a, i) => a + i.quantity, 0);
      const remaining = MAX_JARS_PER_ORDER - totalNow;
      if (remaining <= 0) {
        toast.error(`You can order a maximum of ${MAX_JARS_PER_ORDER} jars per order.`);
        return prev;
      }
      const addQty = Math.min(qty, remaining);
      if (addQty < qty) {
        toast.info(`Added ${addQty} jar${addQty > 1 ? "s" : ""} — limit is ${MAX_JARS_PER_ORDER} per order.`);
      }
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: i.quantity + addQty } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity: addQty,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) => {
      const totalOthers = prev
        .filter((i) => i.id !== id)
        .reduce((a, i) => a + i.quantity, 0);
      const remaining = MAX_JARS_PER_ORDER - totalOthers;
      const nextQty = Math.max(0, Math.min(qty, remaining));
      if (qty > remaining) {
        toast.error(`You can order a maximum of ${MAX_JARS_PER_ORDER} jars per order.`);
      }
      return prev
        .map((i) => (i.id === id ? { ...i, quantity: nextQty } : i))
        .filter((i) => i.quantity > 0);
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((a, i) => a + i.quantity, 0);
    const subtotal = items.reduce((a, i) => a + i.price * i.quantity, 0);
    return { count, subtotal };
  }, [items]);

  const value = {
    items,
    isOpen,
    setIsOpen,
    addItem,
    updateQty,
    removeItem,
    clear,
    ...totals,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
